import type { Clock } from '../application/ports/clock';

/** Where a shortcut applies. `global` works everywhere, the others only in their surface. */
export type ShortcutScope = 'global' | 'list' | 'reader' | 'compose';

/** A key the user pressed, as any platform can describe it. */
export interface KeyPress {
  /** The character or key name, like the DOM `key` property: `j`, `Enter`, `?`. */
  readonly key: string;
  readonly ctrl?: boolean;
  readonly meta?: boolean;
  readonly alt?: boolean;
  readonly shift?: boolean;
}

export interface ShortcutDefinition {
  readonly id: string;
  readonly scope: ShortcutScope;
  /**
   * Bindings in our notation: a single key (`e`), a modifier combination
   * (`mod+k`, where `mod` is ⌘ on macOS and Ctrl elsewhere) or a sequence of
   * two keys typed after each other (`g i`).
   */
  readonly keys: string[];
  /** i18n key of the label shown in the help dialog. */
  readonly labelKey: string;
  /** i18n key of the group it is listed under. */
  readonly groupKey: string;
}

export type ShortcutMatch =
  | { readonly type: 'none' }
  /** The first key of a sequence: the next key decides. */
  | { readonly type: 'pending'; readonly prefix: string }
  | { readonly type: 'match'; readonly id: string };

const SEQUENCE_TIMEOUT_MS = 1_200;

export interface ShortcutRegistryOptions {
  clock: Clock;
  /** ⌘ instead of Ctrl for `mod` bindings. */
  isMac?: boolean;
  sequenceTimeoutMs?: number;
}

/**
 * The one place that knows which keys do what (ADR 0006). It is pure logic: the
 * app listens for key events and asks the registry, the help dialog renders
 * {@link ShortcutRegistry.help}, and the user can rebind or switch shortcuts off.
 */
export class ShortcutRegistry {
  private readonly definitions = new Map<string, ShortcutDefinition>();
  private readonly overrides = new Map<string, string[]>();
  private enabled = true;
  private pending: { prefix: string; at: number } | null = null;

  constructor(private readonly options: ShortcutRegistryOptions) {}

  /** Adds shortcuts; a duplicate id or a binding already taken in the same scope is a programming error. */
  register(definitions: readonly ShortcutDefinition[]): void {
    for (const definition of definitions) {
      if (this.definitions.has(definition.id))
        throw new Error(`Shortcut ${definition.id} is already registered`);
      for (const binding of definition.keys) {
        const clash = this.find(binding, definition.scope);
        if (clash)
          throw new Error(
            `Shortcut ${definition.id} wants "${binding}", which ${clash.id} already uses`,
          );
      }
      this.definitions.set(definition.id, definition);
    }
  }

  /** The user's "keyboard shortcuts" switch. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.pending = null;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Replaces the bindings of single shortcuts with the user's own. */
  setOverrides(overrides: Readonly<Record<string, string[]>>): void {
    this.overrides.clear();
    for (const [id, keys] of Object.entries(overrides)) this.overrides.set(id, [...keys]);
  }

  /** Bindings in force for a shortcut, the user's own where they set some. */
  bindingsOf(id: string): string[] {
    return this.overrides.get(id) ?? this.definitions.get(id)?.keys ?? [];
  }

  /**
   * Answers what a key press means in the scopes currently on screen. Scopes are
   * consulted from the most specific to the least, so `compose` wins over `global`.
   */
  handle(press: KeyPress, activeScopes: readonly ShortcutScope[]): ShortcutMatch {
    if (!this.enabled) return { type: 'none' };
    const key = this.notationOf(press);
    if (!key) return { type: 'none' };

    const pending = this.takePending();
    if (pending) {
      const match = this.findIn(`${pending} ${key}`, activeScopes);
      if (match) return { type: 'match', id: match.id };
    }

    const direct = this.findIn(key, activeScopes);
    if (direct) return { type: 'match', id: direct.id };

    if (this.startsSequence(key, activeScopes)) {
      this.pending = { prefix: key, at: this.options.clock.now() };
      return { type: 'pending', prefix: key };
    }
    return { type: 'none' };
  }

  /** Everything the help dialog shows, grouped in registration order. */
  help(): Array<{
    groupKey: string;
    entries: Array<{ id: string; labelKey: string; keys: string[]; scope: ShortcutScope }>;
  }> {
    const groups = new Map<
      string,
      Array<{ id: string; labelKey: string; keys: string[]; scope: ShortcutScope }>
    >();
    for (const definition of this.definitions.values()) {
      const entries = groups.get(definition.groupKey) ?? [];
      entries.push({
        id: definition.id,
        labelKey: definition.labelKey,
        keys: this.bindingsOf(definition.id),
        scope: definition.scope,
      });
      groups.set(definition.groupKey, entries);
    }
    return [...groups].map(([groupKey, entries]) => ({ groupKey, entries }));
  }

  /** Our notation for a key press, or null for a press that cannot be a shortcut. */
  private notationOf(press: KeyPress): string | null {
    const key = press.key;
    if (key.length === 0 || key === 'Shift' || key === 'Control' || key === 'Meta' || key === 'Alt')
      return null;
    const isCharacter = key.length === 1;
    const parts: string[] = [];
    if (this.options.isMac ? press.meta : press.ctrl) parts.push('mod');
    if (press.alt) parts.push('alt');
    // For characters the shift is already in the character itself (`?`, `#`, `A`).
    if (press.shift && !isCharacter) parts.push('shift');
    parts.push(key);
    return parts.join('+');
  }

  private takePending(): string | null {
    if (!this.pending) return null;
    const timeout = this.options.sequenceTimeoutMs ?? SEQUENCE_TIMEOUT_MS;
    const { prefix, at } = this.pending;
    this.pending = null;
    return this.options.clock.now() - at <= timeout ? prefix : null;
  }

  private startsSequence(key: string, activeScopes: readonly ShortcutScope[]): boolean {
    for (const definition of this.definitions.values()) {
      if (!activeScopes.includes(definition.scope)) continue;
      if (this.bindingsOf(definition.id).some((binding) => binding.startsWith(`${key} `)))
        return true;
    }
    return false;
  }

  private findIn(
    binding: string,
    activeScopes: readonly ShortcutScope[],
  ): ShortcutDefinition | null {
    for (const scope of activeScopes) {
      const match = this.find(binding, scope);
      if (match) return match;
    }
    return null;
  }

  private find(binding: string, scope: ShortcutScope): ShortcutDefinition | null {
    for (const definition of this.definitions.values()) {
      if (definition.scope !== scope) continue;
      if (this.bindingsOf(definition.id).includes(binding)) return definition;
    }
    return null;
  }
}
